import { useOktaAuth } from "@okta/okta-react";
import { AxiosError } from "axios";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import promotionsApi from "../../api/promotions/promotionsApi";
import usePromotionsApi from "../../api/promotions/usePromotionsApi";
import { ConfigContext } from "../../context/ConfigContext";

export interface PromotionCompilationJobStatus {
  promotionId: string;
  accountId: string;
  jobId: string;
  status: "IN_PROGRESS" | "FAILED" | "COMPLETED";
  downloadUrl: string | null;
}

export interface PromotionCompilationCompletedJobStatus
  extends PromotionCompilationJobStatus {
  status: "FAILED" | "COMPLETED";
}

export interface PromotionDownloadStatusesResponse {
  statuses: PromotionCompilationJobStatus[];
}

const usePromotionDownloadStatus = (
  accountId: string,
  jobIds: string[]
): {
  downloadStatuses: PromotionCompilationJobStatus[];
  error: AxiosError | undefined;
} => {
  const { authState } = useOktaAuth();
  const {
    PROMOTION_SERVICE_URL,
    POLLING_INTERVAL = 5000,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_PROMOTIONS,
  } = useContext(ConfigContext);
  const isRBACAuthEnabled =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_PROMOTIONS;
  const promotionsApiHook = usePromotionsApi();
  const [statuses, setStatuses] = useState<PromotionCompilationJobStatus[]>([]);
  const [error, setError] = useState<AxiosError | undefined>(undefined);

  const accessToken = useMemo(() => {
    if (authState && authState.isAuthenticated && authState.accessToken) {
      return authState.accessToken.accessToken;
    }
    return undefined;
  }, [authState?.isAuthenticated, authState?.accessToken]);

  const pollHandler = useCallback(
    (token: string | undefined) => {
      if (jobIds.length === 0 || token === undefined) {
        return Promise.resolve([]);
      }

      return isRBACAuthEnabled
        ? promotionsApiHook
            .getExcelCompilationStatuses(accountId, jobIds)
            .then(({ statuses: fetchedStatuses }) => {
              setError(undefined);
              setStatuses([...fetchedStatuses]);
            })
            .catch((e: AxiosError) => {
              setError(e);
              setStatuses([]);
            })
        : promotionsApi
            .getExcelCompilationStatuses(
              PROMOTION_SERVICE_URL,
              accountId,
              jobIds,
              token
            )
            .then(({ statuses: fetchedStatuses }) => {
              setError(undefined);
              setStatuses([...fetchedStatuses]);
            })
            .catch((e: AxiosError) => {
              setError(e);
              setStatuses([]);
            });
    },
    [jobIds]
  );

  useEffect(() => {
    const interval = setInterval(
      () => pollHandler(accessToken),
      POLLING_INTERVAL
    );
    return () => clearInterval(interval);
  }, [jobIds, accessToken]);

  return { downloadStatuses: statuses, error };
};

export default usePromotionDownloadStatus;
